import Backbone from 'backbone';
import ko from 'knockout';
import _ from 'underscore';
import ReactDOM from 'react-dom';
import React from 'react';
import {
  MessageBar,
  MessageBarButton,
} from '@fluentui/react';
import decimalNumber from '@bingads-webui-component-legacy/humanize/decimal';
import { ReactTemplate } from '@bingads-webui-react/react-template';

import numericAdjustmentTemplate from './numeric-adjustment.pug';

const INCREASE_CHANGE = 'Increase';
const DECREASE_CHANGE = 'Decrease';

/* eslint-disable no-param-reassign */

class BaseNumericAdjustmentView extends Backbone.View {
  initialize({
    observable,
    field,
    enabled,
    i18n,
  }) {
    const changeOptions = [{
      option: INCREASE_CHANGE,
      optionText: i18n.getString('Increase'),
    }, {
      option: DECREASE_CHANGE,
      optionText: i18n.getString('Decrease'),
    }];

    const positiveMax = observable.validators.range.max;
    const negativeMax = Math.abs(observable.validators.range.min);

    observable.validators.type.isValid = (value) => {
      if (enabled()) {
        return /^\d+((\.|,)\d+)?$/.test(value);
      }
      return true;
    };

    // simple form bug adds default decimalLength validator of 2 on initial values
    observable.validators.decimalLength.isValid = () => true;

    const originalRangeValidator = observable.validators.range
      .isValidValue.bind(observable.validators.range);

    observable.validators.range.isValidValue = (value) => {
      if (this.viewModel) {
        this.viewModel.showRecommendationMessageDefault(true);
      }
      if (enabled()) {
        return originalRangeValidator(value);
      }
      return true;
    };

    let initialChangeType;
    observable.validators.range.min = 0;

    if (observable() < 0) {
      initialChangeType = DECREASE_CHANGE;
      observable.validators.range.max = negativeMax;
    } else {
      initialChangeType = INCREASE_CHANGE;
      observable.validators.range.max = positiveMax;
    }

    observable(Math.abs(observable()));

    this.adjustmentValueString = ko.observable(decimalNumber.fromFloatToStringNoTrailingZero(observable()));

    this.viewModel = {
      observable,
      changeOptions: ko.observableArray(changeOptions),
      selectedChange: ko.observable(initialChangeType),
      enabled,
      // recommendation message should be hide when loading UI.
      showRecommendationMessageDefault: ko.observable(false),
      adjustmentValue: ko.computed({
        read() {
          return this.adjustmentValueString();
        },
        write(value) {
          this.adjustmentValueString(value);
          return observable(observable.validators.type.isValid(value) ? decimalNumber.fromLocalToFloat(value) : value);
        },
      }, this),
    };

    this.field = field;
    this.i18n = i18n;
    this.decreaseMaxNum = _.isNumber(field.decreaseMaxNum) ? field.decreaseMaxNum : null;

    this.viewModel.showRecommendationMessage = ko.computed(() => {
      const isShow = this.viewModel.enabled()
        && this.viewModel.showRecommendationMessageDefault()
        && this.viewModel.selectedChange() === DECREASE_CHANGE
        && this.viewModel.observable.validate()
        && _.isNumber(this.decreaseMaxNum)
        && this.viewModel.observable() > this.decreaseMaxNum;
      if (isShow && this.field.logTrace) {
        this.field.logTrace('BCCForImportRecommendation: show');
      }
      return isShow;
    });

    this.viewModel.selectedChange.subscribe((newChangeType) => {
      if (newChangeType === DECREASE_CHANGE) {
        observable.validators.range.max = negativeMax;
      } else {
        observable.validators.range.max = positiveMax;
      }

      observable.validate();
    });
  }

  onApplyRecommendation() {
    if (this.field.logTrace) {
      this.field.logTrace('BCCForImportRecommendation: apply');
      this.field.logTrace(`BCCForImportRecommendation: newvalue ${this.decreaseMaxNum}, oldvalue ${this.viewModel.observable()}`);
    }
    this.viewModel.adjustmentValue(this.decreaseMaxNum);
  }

  renderMessageBar() {
    ReactDOM.render(
      // eslint-disable-next-line react/jsx-filename-extension
      <MessageBar
        className="recommendation"
        styles={{
          root: {
            marginTop: '10px',
            marginLeft: '28px',
            width: '95%',
            background: '#EFF6FC',
          },
          text: {
            fontSize: 14,
          },
          actions: {
            'flex-direction': 'row',
          },
        }}
        actions={
          // eslint-disable-next-line react/jsx-no-bind
          <MessageBarButton onClick={this.onApplyRecommendation.bind(this)}>
            {this.i18n.getString('AdjustmentForCampaignBudgets_ApplyRecommendation')}
          </MessageBarButton>
        }
      >
        <ReactTemplate
          template={this.field.decreaseMaxNumMessageTemplate.replace('{{decreaseMaxNum}}', this.decreaseMaxNum)}
          model={{
            b: text => (<b>{text}</b>),
          }}
        />
      </MessageBar>,
      this.$('.import-options-recommendation-message .message-section')[0]
    );
  }

  render() {
    this.$el.html(numericAdjustmentTemplate({
      field: this.field,
      i18n: this.i18n,
    }));

    ko.applyBindings(this.viewModel, this.el);
    if (_.isNumber(this.decreaseMaxNum)) {
      this.renderMessageBar();
    }
    return this;
  }

  isDecreaseSelected() {
    return this.viewModel.selectedChange() === DECREASE_CHANGE;
  }
}

export function createNumericAdjustmentViewClass(i18n, dependentFieldName) {
  return class NumericAdjustmentView extends BaseNumericAdjustmentView {
    constructor({ observable, viewModel, field }) {
      super({
        observable,
        field,
        enabled: _.propertyOf(viewModel)(dependentFieldName),
        i18n,
      });
    }
  };
}
